import { __rest, __assign } from 'tslib';
import * as React from 'react';
import { UitkLayoutTags } from 'uitk-react-layout';
import { classNames } from 'uitk-react-utils';
import { MinMax, isSmallKey, isAutoRepeatGrid, MaketrackItem } from './functions.esm.js';
import styles from './uitk-layout-grid.css';

var getClassNameList = classNames.bind(styles);
var baseClass = 'uitk-layout-grid';
/**
 * Set a valid grid track value
 *
 * @param value {any}
 * @returns string
 */
var trackList = function (value) {
    var track = [];
    var isArray = Array.isArray(value);
    var isNumber = typeof value === 'number';
    if (isArray) {
        value.forEach(function (item) { return track.push(MaketrackItem(item)); });
    }
    else if (isNumber) {
        track.push("repeat(".concat(value, ", minmax(0, 1fr))")); // Include minmax() to ensure unbroken content doesn't force grid out
    }
    else {
        track.push(MaketrackItem(value));
    }
    return track.join(' ');
};
/**
 * Generate a valid CSS custom prop / value object for grid areas
 *
 * @param areas {UitkLayoutGridArea | UitkLayoutConditionalGridArea}
 * @returns Object as React.CSSProperties
 */
var areasValue = function (areas) {
    var _a;
    var isObject = !Array.isArray(areas) && typeof areas === 'object';
    var obj = {};
    var areaRows = function (value) { return value.map(function (area) { return "\"".concat(area, "\""); }).join(' '); };
    if (isObject) {
        Object.entries(areas).map(function (_a) {
            var _b;
            var key = _a[0], val = _a[1];
            return Object.assign(obj, (_b = {},
                _b["--uitk-layoutgrid-areas".concat(isSmallKey(key) ? '' : "-".concat(key))] = areaRows(val),
                _b));
        });
    }
    else {
        Object.assign(obj, (_a = {}, _a["--uitk-layoutgrid-areas"] = areaRows(areas), _a));
    }
    return obj;
};
/**
 * Generate a valid CSS custom prop / value object for grid gap
 *
 * @param value {UitkLayoutSpace | UitkLayoutGridSpaceInterface}
 * @returns Object as React.CSSProperties
 */
var spaceValue = function (value) {
    var _a, _b;
    var isString = typeof value === 'string';
    var colValue = isString ? value : value.column;
    var rowValue = isString ? value : value.row;
    return __assign(__assign({}, (colValue && (_a = {}, _a["--uitk-layoutgrid-column-gap"] = "var(--uitk-layoutgrid-space-".concat(colValue, ")"), _a))), (rowValue && (_b = {}, _b["--uitk-layoutgrid-row-gap"] = "var(--uitk-layoutgrid-space-".concat(rowValue, ")"), _b)));
};
/**
 * Set a valid value for the deprecated UitkLayoutGridAutoRepeat interface
 *
 * @param repeatType {string} Type of repeat
 * @param minValue {any} min value for the minmax() part of the repeat() function
 * @param suffix {string} suffix for the custom property name
 * @returns object as React.CSSProperties
 */
var autoRepeatGridValue = function (repeatType, minValue, suffix) {
    var _a;
    var newRepeatType = repeatType.replace('_', '-');
    return _a = {},
        _a["--uitk-layoutgrid-".concat(suffix)] = "repeat(".concat(newRepeatType, ", minmax(var(--uitk-layoutgrid-egds-size__").concat(minValue, "), 1fr))"),
        _a;
};
/**
 * Generate a valid CSS custom prop / value object for:
 *
 * - columns
 * - rows
 * - autoColumns
 * - autoRows
 *
 * @param value {UitkLayoutSpace | UitkLayoutGridSpaceInterface}
 * @returns Object as React.CSSProperties
 */
var gridTrackValue = function (value, suffix) {
    var _a;
    var isObject = !Array.isArray(value) && typeof value === 'object';
    var obj = {};
    if (isObject) {
        Object.entries(value).map(function (_a) {
            var _b;
            var key = _a[0], val = _a[1];
            return Object.assign(obj, isAutoRepeatGrid(value)
                ? autoRepeatGridValue(key, val, suffix)
                : (_b = {}, _b["--uitk-layoutgrid-".concat(suffix).concat(isSmallKey(key) ? '' : "-".concat(key))] = trackList(val), _b));
        });
    }
    else {
        Object.assign(obj, (_a = {}, _a["--uitk-layoutgrid-".concat(suffix)] = trackList(value), _a));
    }
    return obj;
};
/**
 * Generate a valid CSS custom prop for blockSize
 *
 * @param value {UitkLayoutGridBlockSize}
 * @param suffix {string}
 * @returns Object as React.CSSProperties
 */
var blockSizeValue = function (value, suffix) {
    var _a;
    var obj = {};
    var newValue;
    if (value !== 'full_size') {
        // If value is a viewport block (vb) length,
        // convert to viewport height (vh) for the time being,
        // until browser support matches our requirements
        // https://caniuse.com/mdn-css_types_length_vb
        newValue = value.replace('vb', 'vh');
        Object.assign(obj, (_a = {}, _a["--uitk-layoutgrid-".concat(suffix)] = newValue, _a));
    }
    return obj;
};
/**
 * Prevent invalid CSS Custom Properties from being applied
 *
 * @param value {UitkLayoutGridArea | UitkLayoutConditionalGridArea | UitkLayoutGridTrack | UitkLayoutConditionalGridTrack}
 * @param suffix {string}
 * @param baseCSSClass {string}
 * @returns getClassNameList()
 */
var conditionalGridTrackClassName = function (value, suffix, baseCSSClass) {
    if (baseCSSClass === void 0) { baseCSSClass = baseClass; }
    var isObject = !Array.isArray(value) && typeof value === 'object';
    var isautoGrid = isObject &&
        (Object.prototype.hasOwnProperty.call(value, 'auto_fill') ||
            Object.prototype.hasOwnProperty.call(value, 'auto_fit'));
    return getClassNameList(isObject
        ? Object.entries(value).map(function (_a) {
            var key = _a[0];
            return "".concat(baseCSSClass, "-has-").concat(suffix).concat(isSmallKey(key) ? '' : "-by-".concat(key));
        })
        : "".concat(baseCSSClass, "-has-").concat(suffix), isautoGrid && "".concat(baseCSSClass, "-has-").concat(suffix, "-using-auto-grid"));
};
var conditionalBlockSizeClassName = function (value, suffix, baseCSSClass) {
    if (baseCSSClass === void 0) { baseCSSClass = baseClass; }
    return (value === 'full_size' ? "".concat(baseCSSClass, "-").concat(suffix, "-").concat(value) : "".concat(baseCSSClass, "-").concat(suffix, "-explicit_size"));
};
var wrapperClassNames = function (_a) {
    var areas = _a.areas, alignContent = _a.alignContent, alignItems = _a.alignItems, autoFlow = _a.autoFlow, autoColumns = _a.autoColumns, autoRows = _a.autoRows, blockSize = _a.blockSize, className = _a.className, columns = _a.columns, display = _a.display, justifyContent = _a.justifyContent, justifyItems = _a.justifyItems, minBlockSize = _a.minBlockSize, rows = _a.rows, space = _a.space;
    return getClassNameList(baseClass, alignContent && "".concat(baseClass, "-align-content-").concat(alignContent), alignItems && "".concat(baseClass, "-align-items-").concat(alignItems), areas && conditionalGridTrackClassName(areas, 'areas'), autoColumns && "".concat(baseClass, "-has-auto-columns"), autoRows && "".concat(baseClass, "-has-auto-rows"), columns && conditionalGridTrackClassName(columns, 'columns'), rows && conditionalGridTrackClassName(rows, 'rows'), space && "".concat(baseClass, "-has-space"), autoFlow && "".concat(baseClass, "-auto-flow-").concat(autoFlow), blockSize && conditionalBlockSizeClassName(blockSize, 'block-size'), display && "".concat(baseClass, "-display-").concat(display), justifyContent && "".concat(baseClass, "-justify-content-").concat(justifyContent), justifyItems && "".concat(baseClass, "-justify-items-").concat(justifyItems), minBlockSize && "".concat(baseClass, "-min-block-size-").concat(minBlockSize), className);
};
var UitkLayoutGrid = function (props) {
    var alignContent = props.alignContent, alignItems = props.alignItems, areas = props.areas, autoColumns = props.autoColumns, autoFlow = props.autoFlow, autoRows = props.autoRows, blockSize = props.blockSize, children = props.children, display = props.display, space = props.space, columns = props.columns, className = props.className, justifyContent = props.justifyContent, justifyItems = props.justifyItems, minBlockSize = props.minBlockSize, rows = props.rows, tag = props.tag, dataTestId = props.dataTestId, style = props.style, attributes = __rest(props, ["alignContent", "alignItems", "areas", "autoColumns", "autoFlow", "autoRows", "blockSize", "children", "display", "space", "columns", "className", "justifyContent", "justifyItems", "minBlockSize", "rows", "tag", "dataTestId", "style"]);
    var wrapperClassNameProps = {
        alignContent: alignContent,
        alignItems: alignItems,
        autoFlow: autoFlow,
        autoColumns: autoColumns,
        autoRows: autoRows,
        areas: areas,
        blockSize: blockSize,
        className: className,
        columns: columns,
        display: display,
        justifyContent: justifyContent,
        justifyItems: justifyItems,
        minBlockSize: minBlockSize,
        rows: rows,
        space: space
    };
    var gridCustomProps = __assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign({}, (areas && areasValue(areas))), (autoColumns && gridTrackValue(autoColumns, 'auto-columns'))), (autoRows && gridTrackValue(autoRows, 'auto-rows'))), (columns && gridTrackValue(columns, 'columns'))), (rows && gridTrackValue(rows, 'rows'))), (space && spaceValue(space))), (blockSize && blockSizeValue(blockSize, 'block-size'))), (style && style));
    // Renders the custom Tag
    var Tag = ((tag && UitkLayoutTags.get(tag)) || UitkLayoutTags.get('div'));
    return children ? (React.createElement(Tag, __assign({ className: wrapperClassNames(__assign({}, wrapperClassNameProps)), style: gridCustomProps }, attributes, (dataTestId && { 'data-test-id': dataTestId })), children)) : null;
};
UitkLayoutGrid.displayName = 'UitkLayoutGrid';
UitkLayoutGrid.defaultProps = {
    autoColumns: [MinMax('0x', '1fr')],
    display: 'grid'
};
var withUitkLayoutGrid = function (WrappedComponent) {
    var WithUitkLayoutGrid = function (props) {
        var alignContent = props.alignContent, alignItems = props.alignItems, autoColumns = props.autoColumns, autoFlow = props.autoFlow, autoRows = props.autoRows, areas = props.areas, blockSize = props.blockSize, className = props.className, children = props.children, columns = props.columns, display = props.display, justifyContent = props.justifyContent, justifyItems = props.justifyItems, minBlockSize = props.minBlockSize, rows = props.rows, space = props.space, style = props.style;
        var wrapperClassNameProps = {
            alignContent: alignContent,
            alignItems: alignItems,
            autoFlow: autoFlow,
            autoColumns: autoColumns,
            autoRows: autoRows,
            areas: areas,
            blockSize: blockSize,
            className: className,
            columns: columns,
            display: display,
            justifyContent: justifyContent,
            justifyItems: justifyItems,
            minBlockSize: minBlockSize,
            rows: rows,
            space: space
        };
        var gridCustomProps = __assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign({}, (areas && areasValue(areas))), (autoColumns && gridTrackValue(autoColumns, 'auto-columns'))), (autoRows && gridTrackValue(autoRows, 'auto-rows'))), (columns && gridTrackValue(columns, 'columns'))), (rows && gridTrackValue(rows, 'rows'))), (space && spaceValue(space))), (blockSize && blockSizeValue(blockSize, 'block-size'))), (style && style));
        return (React.createElement(WrappedComponent, { className: wrapperClassNames(__assign({}, wrapperClassNameProps)), style: gridCustomProps }, children || null));
    };
    WithUitkLayoutGrid.displayName = "withUitkLayoutGrid(".concat(WrappedComponent.displayName, ")");
    return WithUitkLayoutGrid;
};

export { UitkLayoutGrid, withUitkLayoutGrid };
